import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ShoppingBag, Shield, AlertCircle, Users, Package, ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";
import { formatMoney } from "../../utils/formatMoney";

export default function ComparisonDrawer({ open, onClose, policies = [] }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);

  // Track selected route/package - only one can be selected at a time across all policies
  const [selectedItem, setSelectedItem] = useState(null); // { policyId, item }

  if (!open || policies.length < 2) return null;

  // Helper to format currency
  const formatCurrency = (amount) => {
    if (!amount) return "N/A";
    return formatMoney(amount);
  };

  // Helper to format duration
  const formatDuration = (duration) => {
    if (!duration) return "N/A";
    
    const durationStr = String(duration).toLowerCase();
    
    const match = durationStr.match(/^(\d+)\s*(daily|weekly|monthly|yearly|day|week|month|year)s?$/);
    
    if (!match) return duration;
    
    const value = parseInt(match[1]);
    let unit = match[2];
    
    const unitMap = {
      'daily': 'day',
      'weekly': 'week', 
      'monthly': 'month',
      'yearly': 'year'
    };
    
    unit = unitMap[unit] || unit;
    
    if (value > 1) {
      unit = unit + 's';
    }
    
    return `${value} ${unit}`;
  };

  // Helper to normalize premium duration
  const normalizePremiumDuration = (duration) => {
    if (!duration) return null;
    const durationStr = String(duration).toLowerCase().trim();
    const durationMap = {
      'yearly': 'year',
      'monthly': 'month',
      'weekly': 'week',
      'daily': 'day'
    };
    return durationMap[durationStr] || durationStr;
  };

  // Helper to check if policy is TripSecure
  const isTripSecure = (policy) => {
    return policy.shortDescription === "tripsecure_insurance";
  };

  // Helper to get packages from policy
  const getPackages = (policy) => {
    if (!policy.originalPolicy?.coverage_durations) return [];
    
    const isLifeInsurance = policy.shortDescription === "life_insurance";
    const isGeneralInsurance = policy.shortDescription === "general_insurance";
    const isTravelInsurance = policy.shortDescription === "travel_insurance";

    if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
      return policy.originalPolicy.coverage_durations;
    }
    
    return [];
  };

  // Helper to get routes for TripSecure insurance
  const getRoutes = (policy) => {
    if (!isTripSecure(policy)) return [];
    return policy.originalPolicy?.coverage_durations || [];
  };

  // Handle route/package selection
  const handleSelectItem = (policyId, item) => {
    if (selectedItem?.policyId === policyId && selectedItem?.item === item) {
      setSelectedItem(null);
    } else {
      setSelectedItem({ policyId, item });
    }
  };

  // Handle purchase - navigate to checkout with registrant selection
  const handlePurchase = (policy) => {
    const packages = getPackages(policy);
    const isTripSecurePolicy = isTripSecure(policy);
    const policySelectedItem = selectedItem?.policyId === policy.id ? selectedItem.item : null;
    
    if (packages.length > 0) {
      // For Life, General, Travel with packages - open drawer to select package
      onClose();
      if (window.openPolicyPackageDrawer) {
        window.openPolicyPackageDrawer(policy);
      }
    } else if (isTripSecurePolicy && policySelectedItem) {
      // For TripSecure with selected route
      const insuranceProviderSlug = policy?.originalPolicy?.insurance_provider 
        ? (typeof policy.originalPolicy.insurance_provider === 'object' 
            ? policy.originalPolicy.insurance_provider.slug || policy.originalPolicy.insurance_provider.id || null
            : policy.originalPolicy.insurance_provider)
        : null;
      const details = {
        policyType: policy.originalPolicy?.policy_type || "intercity_travel",
        providerName: policy.provider || "",
        policyName: policy.policyName || "",
        policySlug: policy.originalPolicy?.slug || policy.id,
        originalPolicy: policy.originalPolicy,
        coverageDuration: policySelectedItem,
        insurance_provider_slug: insuranceProviderSlug,
      };
      
      navigate("/checkout", { state: details });
    } else {
      // For policies without packages
      const insuranceProviderSlug = policy?.originalPolicy?.insurance_provider 
        ? (typeof policy.originalPolicy.insurance_provider === 'object' 
            ? policy.originalPolicy.insurance_provider.slug || policy.originalPolicy.insurance_provider.id || null
            : policy.originalPolicy.insurance_provider)
        : null;
      const details = {
        policyType: policy.originalPolicy?.policy_type || "daily_commuter",
        providerName: policy.provider || "",
        policyName: policy.policyName || "",
        policySlug: policy.originalPolicy?.slug || policy.id,
        originalPolicy: policy.originalPolicy,
        insurance_provider_slug: insuranceProviderSlug,
      };
      
      navigate("/checkout", { state: details });
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed inset-y-0 right-0 z-[70] w-full max-w-7xl bg-white shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-emerald-500 to-green-600 border-b border-emerald-600">
              <div className="flex justify-between items-center">
                <div className="flex gap-3 items-center">
                  <div className="flex justify-center items-center w-10 h-10 bg-white rounded-lg">
                    <Shield className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Policy Comparison</h2>
                    <p className="text-sm text-emerald-50">
                      Comparing {policies.length} insurance policies side by side
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-white rounded-lg transition-colors hover:bg-white/20"
                  aria-label="Close comparison"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Comparison Content */}
            <div className="overflow-auto flex-1 p-6 bg-gray-50">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {policies.map((policy, policyIdx) => {
                  const packages = getPackages(policy);
                  const routes = getRoutes(policy);
                  const isTripSecurePolicy = isTripSecure(policy);
                  const hasSelection = selectedItem?.policyId === policy.id;
                  const requiresSelection = (isTripSecurePolicy && routes.length > 0) || packages.length > 0;
                  const canProceed = !requiresSelection || hasSelection || packages.length > 0;
                  
                  const isLeftCard = policyIdx === 0;
                  const headerColors = isLeftCard 
                    ? "bg-gradient-to-r from-blue-100 to-indigo-100"
                    : "bg-gradient-to-r from-teal-100 to-cyan-100";
                  
                  return (
                    <motion.div
                      key={policy.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`overflow-hidden bg-white rounded-xl border-2 shadow-lg ${
                        isLeftCard ? "border-blue-200" : "border-teal-200"
                      }`}
                    >
                      {/* Policy Header */}
                      <div className={`p-6 ${headerColors}`}>
                        <h3 className={`mb-2 text-xl font-bold ${isLeftCard ? "text-blue-900" : "text-teal-900"}`}>
                          {policy.policyName}
                        </h3>
                        <p className={`text-sm ${isLeftCard ? "text-blue-700" : "text-teal-700"}`}>
                          {policy.provider}
                        </p>
                        <div className="mt-4">
                          <div className="flex gap-2 items-baseline">
                            <span className={`text-3xl font-bold ${isLeftCard ? "text-blue-900" : "text-teal-900"}`}>
                              {formatCurrency(policy.premiumMin)}
                            </span>
                            {policy.premiumMax && policy.premiumMax !== policy.premiumMin && (
                              <span className={isLeftCard ? "text-blue-700" : "text-teal-700"}>
                                {" "}- {formatCurrency(policy.premiumMax)}
                              </span>
                            )}
                          </div>
                          <p className={`mt-1 text-sm ${isLeftCard ? "text-blue-700" : "text-teal-700"}`}>
                            Premium Range
                          </p>
                        </div>
                        
                        {/* Selection Guideline */}
                        {requiresSelection && !hasSelection && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`mt-4 p-3 rounded-lg border-2 border-dashed ${
                              isLeftCard ? "border-blue-300 bg-blue-50/50" : "border-teal-300 bg-teal-50/50"
                            }`}
                          >
                            <div className="flex gap-2 items-start">
                              <Sparkles className={`w-4 h-4 mt-0.5 ${isLeftCard ? "text-blue-600" : "text-teal-600"}`} />
                              <div>
                                <p className={`text-xs font-semibold ${isLeftCard ? "text-blue-900" : "text-teal-900"}`}>
                                  {isTripSecurePolicy ? "Select Your Route" : "Choose a Package"}
                                </p>
                                <p className={`text-[11px] mt-0.5 ${isLeftCard ? "text-blue-700" : "text-teal-700"}`}>
                                  {isTripSecurePolicy 
                                    ? "Click on any route below to activate the purchase button"
                                    : "Click on a package below to continue"}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )}

                        {/* Success Message */}
                        {hasSelection && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className={`mt-4 p-3 rounded-lg ${
                              isLeftCard ? "bg-blue-50 border border-blue-200" : "bg-teal-50 border border-teal-200"
                            }`}
                          >
                            <div className="flex gap-2 items-center">
                              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                                isLeftCard ? "bg-blue-600" : "bg-teal-600"
                              }`}>
                                <Check className="w-3 h-3 text-white" />
                              </div>
                              <div className="flex-1">
                                <p className={`text-xs font-semibold ${isLeftCard ? "text-blue-900" : "text-teal-900"}`}>
                                  Selection Confirmed
                                </p>
                                <p className={`text-[11px] ${isLeftCard ? "text-blue-700" : "text-teal-700"}`}>
                                  Ready to proceed to checkout
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )}

                        {/* Purchase Button */}
                        <button
                          onClick={() => handlePurchase(policy)}
                          disabled={!canProceed}
                          className={`mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all shadow-sm relative overflow-hidden group ${
                            canProceed
                              ? isLeftCard 
                                ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-blue-200 hover:shadow-lg hover:shadow-blue-300 hover:-translate-y-0.5" 
                                : "bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white shadow-teal-200 hover:shadow-lg hover:shadow-teal-300 hover:-translate-y-0.5"
                              : "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                          }`}
                        >
                          {canProceed && (
                            <motion.div
                              className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10"
                              initial={false}
                              animate={{ opacity: [0, 0.1, 0] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                            />
                          )}
                          <ShoppingBag className="relative z-10 w-4 h-4" />
                          <span className="relative z-10">
                            {packages.length > 0 ? "View Packages" : "Select This Policy"}
                          </span>
                          {canProceed && <ArrowRight className="relative z-10 w-4 h-4 transition-transform group-hover:translate-x-1" />}
                        </button>
                      </div>

                      {/* Policy Content - Similar structure to consumer version but simplified */}
                      <div className="p-6 space-y-6">
                        {/* TripSecure Routes */}
                        {isTripSecurePolicy && routes.length > 0 && (
                          <div>
                            <div className="flex gap-2 items-center mb-3">
                              <Package className={`w-5 h-5 ${isLeftCard ? "text-blue-600" : "text-teal-600"}`} />
                              <h4 className="font-semibold text-gray-900">
                                Available Routes ({routes.length})
                              </h4>
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              {routes.map((route, idx) => {
                                const destinationRegion = route.route_destination || "";
                                const routeLabel = destinationRegion.includes(" - ") 
                                  ? destinationRegion.split(" - ")[1] || destinationRegion
                                  : destinationRegion;
                                
                                const isSelected = selectedItem?.policyId === policy.id && selectedItem?.item === route;
                                const isOtherPolicySelected = selectedItem && selectedItem.policyId !== policy.id;
                                
                                return (
                                  <motion.div
                                    key={idx}
                                    whileHover={{ scale: isOtherPolicySelected ? 1 : 1.02 }}
                                    whileTap={{ scale: isOtherPolicySelected ? 1 : 0.98 }}
                                    className={`p-3 rounded-lg border-2 transition-all relative ${
                                      isSelected
                                        ? isLeftCard 
                                          ? "bg-gradient-to-br from-blue-100 to-indigo-100 border-blue-500 shadow-lg cursor-pointer shadow-blue-200" 
                                          : "bg-gradient-to-br from-teal-100 to-cyan-100 border-teal-500 shadow-lg cursor-pointer shadow-teal-200"
                                        : isOtherPolicySelected
                                          ? "bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed"
                                          : isLeftCard 
                                            ? "bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 cursor-pointer hover:border-blue-400 hover:shadow-md" 
                                            : "bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200 cursor-pointer hover:border-teal-400 hover:shadow-md"
                                    }`}
                                    onClick={() => !isOtherPolicySelected && handleSelectItem(policy.id, route)}
                                  >
                                    {isSelected && (
                                      <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
                                          isLeftCard ? "bg-blue-600" : "bg-teal-600"
                                        }`}
                                      >
                                        <Check className="w-4 h-4 text-white" />
                                      </motion.div>
                                    )}
                                    
                                    {isOtherPolicySelected && (
                                      <div className="absolute inset-0 rounded-lg bg-white/40" />
                                    )}

                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="mb-1">
                                          <p className="text-xs text-gray-500 mb-0.5">From</p>
                                          <p className="text-xs font-semibold text-gray-700">Greater Accra</p>
                                        </div>
                                        <div className="mb-1">
                                          <p className="text-xs text-gray-500 mb-0.5">To</p>
                                          <h5 className={`text-sm font-bold transition-colors ${
                                            isSelected 
                                              ? isLeftCard ? "text-blue-900" : "text-teal-900"
                                              : "text-gray-900"
                                          }`}>
                                            {routeLabel}
                                          </h5>
                                        </div>
                                        <p className={`text-xs mt-1 capitalize font-medium ${
                                          isSelected
                                            ? isLeftCard ? "text-blue-700" : "text-teal-700"
                                            : "text-gray-600"
                                        }`}>
                                          {route.route_type} Route
                                        </p>
                                      </div>
                                      <div className="ml-2 text-right">
                                        <div className={`text-lg font-bold transition-colors ${
                                          isSelected
                                            ? isLeftCard ? "text-blue-700" : "text-teal-700"
                                            : isLeftCard ? "text-blue-600" : "text-teal-600"
                                        }`}>
                                          {formatCurrency(route.price)}
                                        </div>
                                        <div className={`text-xs font-medium ${
                                          isSelected
                                            ? isLeftCard ? "text-blue-600" : "text-teal-600"
                                            : isLeftCard ? "text-blue-700" : "text-teal-700"
                                        }`}>
                                          {route.display_value} {route.display_unit}
                                        </div>
                                      </div>
                                    </div>
                                  </motion.div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Coverage Benefits */}
                        {policy.originalPolicy?.insurance_coverage_list && policy.originalPolicy.insurance_coverage_list.length > 0 && (
                          <div>
                            <div className="flex gap-2 items-center mb-3">
                              <Shield className={`w-5 h-5 ${isLeftCard ? "text-blue-600" : "text-teal-600"}`} />
                              <h4 className="font-semibold text-gray-900">Coverage Benefits</h4>
                            </div>
                            <ul className="space-y-2">
                              {policy.originalPolicy.insurance_coverage_list.map((benefit, bIdx) => (
                                <li key={bIdx} className="flex gap-2 items-start text-sm">
                                  <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1">
                                    <span className="font-medium text-gray-900">{benefit.title}</span>
                                    {benefit.limit && (
                                      <span className="ml-2 font-bold text-emerald-600">
                                        (₵{benefit.limit})
                                      </span>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Eligibility */}
                        {(policy.eligibilityCriteria || policy.originalPolicy?.eligibility_criteria) && (
                          <div>
                            <div className="flex gap-2 items-center mb-2">
                              <Users className="w-5 h-5 text-indigo-600" />
                              <h4 className="font-semibold text-gray-900">Eligibility</h4>
                            </div>
                            <ul className="space-y-2">
                              {(policy.eligibilityCriteria || policy.originalPolicy?.eligibility_criteria || []).map((criteria, idx) => (
                                <li key={idx} className="flex gap-2 items-start text-sm text-gray-700">
                                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                  <span>{criteria}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-4 bg-white border-t border-gray-200">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <p className="text-sm text-gray-600">
                  Review the details above and select the policy that best suits your needs
                </p>
                <button
                  onClick={onClose}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                >
                  Close Comparison
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
